import { Injectable } from '@angular/core';
import { CanActivate, UrlTree } from '@angular/router';
import { LoginService } from '@login/services/login.service';
import { select, Store } from '@ngrx/store';
import { SharedService } from '@shared/shared.service';
import { EMPTY, Observable, of, switchMap, tap } from 'rxjs';
import { catchError, filter, first, map, take } from 'rxjs/operators';
import * as loginActions from '@login/ngrx/login.actions';
import * as selectFromLogin from '@login/ngrx/login.selectors';
import * as jwt_decode from 'jwt-decode';
import {WebOwnProfileModel} from "@services/back-office/model/webOwnProfileModel";

@Injectable({
  providedIn: 'root'
})
export class UserGuardService implements CanActivate {
  constructor(
    private store$: Store,
    private loginService: LoginService,
  ) {
    this.store$.dispatch(new loginActions.LoadUser());
  }

  getFromStoreOrApi(): Observable<WebOwnProfileModel | null> {
    return this.store$.pipe(
      select(selectFromLogin.selectCurrentUser),
      tap((data: WebOwnProfileModel | null) => {
        if (!data) {
          this.loginService.loadCurrentUser().pipe(
            filter(response => !!response),
            take(1),
            map((user: WebOwnProfileModel) => {
              this.store$.dispatch(new loginActions.SetUser(user));
            }),
            catchError(() => EMPTY),
          ).subscribe();
        }
      }),
      filter(data => !!data),
      take(1)
    );
  }

  canActivate(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    /** Get Permissions from token if exists */
    const token = localStorage.getItem('token');

    if (token) {
      const tokenPermissions: any = jwt_decode.default(token);
      /** Get HEX strings */
      const hexStrings: string[] = [];
      let hexStringWithAssociations: {association: string, hex: string}[] = [];

      if (tokenPermissions && tokenPermissions.permissions) {
        // @ts-ignore
        Object.entries(tokenPermissions.permissions).forEach(([hexString, associationsArray]: [string, {t: string, bp?: string[]}[]]) => {
          hexStrings.push(hexString);
          /** Get Associations IDs */
          const itemAssociationsWithHex = associationsArray && associationsArray.length ?
            associationsArray.map((item: {t: string, bp?: string[]}) => {
              return {association: item.t, hex: hexString, ...item.bp && {bp: item.bp}};
            }) : null;

          if (itemAssociationsWithHex) {
            hexStringWithAssociations = [...hexStringWithAssociations, ...itemAssociationsWithHex];
          }
        });
      }

      /** Get Permissions by HEX string */
      this.loginService.getPermissionsByHex(hexStrings).pipe(
        filter(data => !!data),
        first(),
        map((result: any) => {
          this.store$.dispatch(
            new loginActions.SetPermissions(SharedService.transformDataToPermissions(hexStringWithAssociations, result))
          );
        }),
        catchError(() => EMPTY)
      ).subscribe();
    }

    return this.getFromStoreOrApi().pipe(
      switchMap(() => of(true)),
      catchError(() => of(false)),
    );
  }
}
